import styled from '@emotion/styled';
import Image from '@base/Image';
import Button from 'aquilla/core/molecules/Button';
import { Fonts, mediaQuery, Spacings } from '@styles/index';

const CodeContainer = styled.div`
  margin-bottom: ${Spacings.SPACING_3B};
  padding: 0 ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_2B};
  background: ${({ highlight }) =>
    highlight
      ? 'linear-gradient(104.03deg, var(--color-i-orange-21) 0%, var(--color-base-1) 100%);'
      : 'var(--color-base-1)'};
  position: relative;
  border: 1px solid var(--color-i-divider);

  &:first-of-type {
    margin-top: ${Spacings.SPACING_2B};
  }

  &:last-of-type {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const Circle = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ $isRight }) =>
    $isRight
      ? 'linear-gradient(270deg, var(--color-i-gray-37) 49.999%, var(--color-i-divider) 50%)'
      : 'linear-gradient(270deg, var(--color-i-divider) 49.999%, var(--color-i-gray-37) 50%)'};
  top: 69px;
  left: ${({ $isRight }) => ($isRight ? '' : '-8px')};
  right: ${({ $isRight }) => ($isRight ? '-8px' : '')};
`;

const InnerCircle = styled.div`
  width: 14px;
  height: 14px;
  margin: 1px;
  border-radius: 50%;
  background: var(--color-base-light-0);
`;

const CodeDetails = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_3B};
  border-bottom: 1px dashed var(--color-i-divider);
  align-items: center;
  position: relative;
`;

const Wrap = styled.div`
  margin-left: ${Spacings.SPACING_3B};
`;

const Percentage = styled.div`
  font-size: ${Fonts.H5};
  line-height: 24px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const CodeValue = styled.div`
  font-size: ${Fonts.H6};
  line-height: 20px;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};
  max-width: ${Spacings.SPACING_70B};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${mediaQuery.uptoTablet} {
    max-width: ${Spacings.SPACING_40B};
  }
`;

const ApplyCodeBtn = styled(Button)`
  width: 73px;
  min-width: 73px;
  padding: 10px 18px;
  height: 32px;
  min-height: 32px;
  border-color: var(--color-text-primary);
  position: absolute;
  top: ${Spacings.SPACING_4B};
  right: 0;
  font-size: ${Fonts.H7};
  line-height: 16px;

  & .button--bounce-loader {
    position: absolute;
  }
`;

const Points = styled.div`
  padding: ${Spacings.SPACING_4B} 0;
`;

const Point = styled.div`
  margin-bottom: 6px;
  display: flex;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Text = styled.div`
  font-size: ${Fonts.H7};
  line-height: 16px;
  color: var(--color-text-secondary);
  margin-left: ${Spacings.SPACING_1B};
`;

const getIcon = (referralType, isActive) => {
  if (!isActive) return 'checkout/referral-codes/in-active.svg';

  switch (referralType) {
    case 1:
      return 'checkout/referral-codes/learner.svg';

    case 2:
      return 'checkout/referral-codes/educator.svg';

    case 4:
    case 6:
      return 'checkout/referral-codes/event.svg';

    case 5:
      return 'checkout/referral-codes/renew.svg';

    default:
      return 'checkout/referral-codes/custom.svg';
  }
};

const SuggestedCode = ({
  code: { discountPercent, code, info, referralType },
  onApply,
  isActive = false,
  isFirst = false,
  referralCode = '',
  validateReferralInProgress = false
}) => {
  return (
    <CodeContainer highlight={isFirst}>
      <Circle>
        <InnerCircle />
      </Circle>
      <Circle $isRight>
        <InnerCircle />
      </Circle>
      <CodeDetails>
        <Image
          src={getIcon(referralType, isActive)}
          alt="code-icon"
          width={32}
          height={32}
        />
        <Wrap>
          <Percentage>{discountPercent}% off</Percentage>
          <CodeValue>{String(code ?? '').toUpperCase()}</CodeValue>
        </Wrap>
        {isActive && (
          <ApplyCodeBtn
            label="Apply"
            variant="secondary"
            color="black"
            onClick={() => onApply(code)}
            isLoading={referralCode === code && validateReferralInProgress}
          />
        )}
      </CodeDetails>
      <Points>
        {info.map(({ text, isApplicable }) => {
          return (
            <Point key={text}>
              <Image
                src={
                  isApplicable
                    ? 'checkout/referral-codes/applicable.svg'
                    : 'checkout/referral-codes/not-applicable.svg'
                }
                alt="applicablity"
                width={16}
                height={16}
              />
              <Text>{text}</Text>
            </Point>
          );
        })}
      </Points>
    </CodeContainer>
  );
};

export default SuggestedCode;
